import { inject, Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import {
	fnCallable,
	type SubmitResponse,
	type AllStatusDetailsResponse,
	type GetDocumentResponse,
	type CancelResponse,
	type AllStatusDetailsRequest,
	type CancelRequest,
	type GetDocumentRequest,
} from '@fnModels/chicago';
import type {
	SubmitData,
	SubmitRequest,
} from '@models/title-insurance/chicago';
import { Logger } from '../core/logger';
import { catchError, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ChicagoFunctions {
	private fns = inject(AngularFireFunctions);

	submitData = (
		lawyerId: string,
		data: SubmitData,
		matterId: string,
		fileNumber?: number,
		clerkId?: string,
	) =>
		this.makeCallable<SubmitResponse, SubmitRequest>(fnCallable.submitData, {
			lawyerId,
			clerkId,
			matterId,
			data,
			fileNumber,
		});

	getAllStatusDetails = (FileNumber: number, matterId: string) =>
		this.makeCallable<AllStatusDetailsResponse, AllStatusDetailsRequest>(
			fnCallable.getAllStatusDetails,
			{ FileNumber, matterId },
		);

	getDocument = (StorageId: number, LawyerId: string) =>
		this.makeCallable<GetDocumentResponse, GetDocumentRequest>(
			fnCallable.getDocument,
			{
				StorageId,
				LawyerId,
			},
		);

	cancel = (
		FileNumber: number,
		CancellationCode: number,
		CancellationText: string,
	) =>
		this.makeCallable<CancelResponse, CancelRequest>(fnCallable.cancelOrder, {
			FileNumber,
			CancellationCode,
			CancellationText,
		});

	private makeCallable = <R, T = unknown>(name: string, requestBody: T) => {
		Logger.info(
			`${ChicagoFunctions.name} - invoking function of type: ${name}`,
			{ requestBody },
		);

		return this.fns
			.httpsCallable<T, R>(name)(requestBody)
			.pipe(
				catchError((error) => {
					Logger.exception(error, {
						message: `${ChicagoFunctions.name} - error invoking function of type: ${name}`,
					});
					return throwError(() => error);
				}),
			);
	};
}
